// Workloads, the list (02-DESIGN/04-workloads/04-placement-records.md § the
// console surface; 01-the-console.md § the words, the Workloads row of § the
// surfaces): an admin's page (hq decision 0047) — every workload of the
// workspace in one table, with no *Yours* · *Everyone's* filter, a solution's
// workload marked with its solution; name, state in a plain word with "n of m
// running" where it runs as several copies (decision 0035) — the image,
// resources and id on the workload's page, not here (01-the-console.md § the
// moves, page by page: Workloads); *Add a workload* a form in the list's place, with its count
// (01-the-console.md § conventions: a form).

import { Plus } from "lucide-react";
import { useState } from "react";

import { tenantPath } from "../../address/address.ts";
import {
  Button,
  Empty,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  plural,
  TextInput,
  useLoad,
} from "../../ui/components.tsx";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Table, type Column } from "../../ui/table.tsx";
import {
  idSubject,
  listSubject,
  registerSubject,
  RegistrationState,
  SERVICE,
  type ListReply,
  type RegisterReply,
  type RegisterRequest,
  type StateReply,
  type Workload,
} from "../../wire/workloads.ts";
import { useInstalled } from "../solutions/installed.ts";
import { computeCheck, computeOf } from "../settings/plan.ts";
import { PlanLink, PlanRefused, usePlan } from "../settings/PlanNotes.tsx";
import { useSurface } from "../surface.tsx";
import { useManaged } from "../workspaces/managed.ts";
import {
  configurationOf,
  copiesOf,
  COPIES_HINT,
  egressOf,
  namesOf,
  PairsEditor,
  RulesEditor,
  type Pair,
} from "./editors.tsx";
import { workloadActs, workloadMenu } from "./acts.ts";
import { plainState, runningCount, whyNot } from "./state.ts";

export function WorkloadsPage() {
  const s = useSurface();
  const managed = useManaged();
  const installed = useInstalled(s.bus);
  const [list, reload] = useLoad("workloads", () => s.bus.call<ListReply>(SERVICE, listSubject(), undefined));
  const form = useFormState<"add">();
  const [mark, setMark] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);

  const all = list.state === "ready" ? list.value.workloads : [];
  // A solution's workload is marked with the solution's own name, as its manifest displays it.
  const solutionOf = (w: Workload) =>
    w.solution === undefined
      ? undefined
      : (installed.find((r) => r.name === w.solution?.name)?.manifest?.display ?? w.solution.name);

  const add = s.offered("workloads:register") ? (
    <Button
      kind="primary"
      onClick={() => {
        form.show("add");
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Add a workload
    </Button>
  ) : undefined;
  const requiredBy = managed.by || "the blueprint";
  const active = (w: Workload) => w.state === RegistrationState.active;
  const act = (w: Workload, verb: "retire" | "suspend" | "resume") => {
    setErr(undefined);
    s.bus.call<StateReply>(SERVICE, idSubject(verb, w.id), undefined, 30000).then(
      () => {
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };
  const sub = (w: Workload) => {
    const solution = solutionOf(w);
    return solution !== undefined
      ? `part of ${solution}`
      : w.registrant === s.me
        ? "by you"
        : `by ${w.display ?? w.registrant}`;
  };
  const columns: Column<Workload>[] = [
    {
      key: "workload",
      header: "Workload",
      width: "minmax(0, 1.5fr)",
      value: (w) => w.name,
      text: (w) => `${w.name} ${sub(w)}`,
      cell: (w) => (
        <span className="cell">
          <span className="cell-main">{w.name}</span>
          <span className="cell-sub">{sub(w)}</span>
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "minmax(140px, 1fr)",
      facet: "Status",
      value: (w) => plainState(w).word,
      text: (w) => `${plainState(w).word} ${runningCount(w)} ${active(w) ? whyNot(w.placement) : ""}`,
      cell: (w) => {
        const p = plainState(w);
        // Why it cannot start, as the service names it, beside the status (decision 0034).
        const why = active(w) ? whyNot(w.placement) : "";
        const failures = w.placement.failures ?? 0;
        const count = [runningCount(w), why, failures > 0 ? plural(failures, "failure") : ""]
          .filter((x) => x !== "")
          .join(" · ");
        return (
          <span className="cell">
            <span>
              <Pill tone={p.tone}>{p.word}</Pill>
              {w.required === true && <Pill strong>required by {requiredBy}</Pill>}
            </span>
            {count !== "" && <span className="cell-sub">{count}</span>}
          </span>
        );
      },
    },
  ];

  if (form.open === "add" && add !== undefined) {
    return (
      <AddWorkload
        registered={list.state === "ready" ? list.value.workloads : undefined}
        onClose={form.close}
        onAdded={(id) => {
          // Back to the list, the new workload marked (01-the-console.md § conventions: add).
          form.close();
          setMark(id);
          reload();
        }}
      />
    );
  }

  return (
    <>
      <PageHead title="Workloads" line={`What runs for ${s.name}.`} action={add} />
      {list.state === "pending" && <Pending what="Reading the workloads" />}
      {list.state === "refused" && <NotAllowed err={list.err} what="Workloads" />}
      {list.state === "ready" && all.length === 0 && (
        <Empty action={add}>{`Nothing runs for ${s.name} yet.`}</Empty>
      )}
      {err !== undefined && <NotAllowed err={err} />}
      {all.length > 0 && (
        <Table
          label="Workloads"
          rows={all}
          rowKey={(w) => w.id}
          sort={{ key: "workload", dir: "asc" }}
          to={(w) => tenantPath(s.tenant, "workloads", w.id)}
          columns={columns}
          mark={mark}
          menu={(w) =>
            workloadMenu(w, workloadActs(w, s), requiredBy, (verb) => {
              act(w, verb);
            })
          }
        />
      )}
    </>
  );
}

function AddWorkload({
  registered,
  onClose,
  onAdded,
}: {
  /** The workspace's registrations, which the plan's room is counted from; undefined until read. */
  registered: readonly Workload[] | undefined;
  onClose: () => void;
  onAdded: (id: string) => void;
}) {
  const s = useSurface();
  const plan = usePlan();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [cpus, setCpus] = useState("1");
  const [memory, setMemory] = useState("512");
  const [count, setCount] = useState("1");
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [rules, setRules] = useState<string[]>([]);
  const [names, setNames] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  // What the plan leaves room for, said before the form is submitted
  // (02-DESIGN/17-plans/02-features-and-limits.md § the console).
  const copies = Number(count);
  const room = computeCheck(
    plan.tenant,
    plan.holder,
    registered === undefined ? undefined : computeOf(registered),
    {
      cpus: Number(cpus) || 0,
      memoryMiB: Number(memory) || 0,
      copies: Number.isInteger(copies) && copies > 0 ? copies : 1,
    },
  );
  const ready = name.trim() !== "" && image.trim() !== "" && room.refusal === undefined;

  const submit = () => {
    if (busy || !ready) {
      return;
    }
    setErr(undefined);
    const configuration = configurationOf(pairs);
    if (configuration instanceof Error) {
      setErr(configuration);
      return;
    }
    const egress = egressOf(rules);
    if (egress instanceof Error) {
      setErr(egress);
      return;
    }
    const declared = namesOf(names);
    if (declared instanceof Error) {
      setErr(declared);
      return;
    }
    const replicas = copiesOf(count);
    if (replicas instanceof Error) {
      setErr(replicas);
      return;
    }
    const body: RegisterRequest = {
      name: name.trim(),
      image: image.trim(),
      resources: { cpus: Number(cpus), memory_mib: Number(memory) },
    };
    if (replicas > 1) {
      body.replicas = replicas;
    }
    if (description.trim() !== "") {
      body.description = description.trim();
    }
    if (Object.keys(configuration).length > 0) {
      body.configuration = configuration;
    }
    if (egress.length > 0) {
      body.egress = egress;
    }
    if (declared.length > 0) {
      body.names = declared;
    }
    setBusy(true);
    s.bus.call<RegisterReply>(SERVICE, registerSubject(), body, 60000).then(
      (reply) => {
        setBusy(false);
        onAdded(reply.id);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };

  return (
    <FormPage
      title="Add a workload"
      back="Workloads"
      line={`A process Impire runs for ${s.name} from an image you name: a service, an agent, a toolkit.`}
      onClose={onClose}
      foot={
        <>
          {busy && <span className="small faint">The image is being looked up at its registry.</span>}
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !ready}>
            {busy ? "Adding…" : "Add"}
          </Button>
        </>
      }
    >
      <p className="muted">It reaches Impire and what you list below, and nothing else.</p>
      {err !== undefined && <PlanRefused err={err} what="Add" holder={plan.holder} />}
      {room.refusal !== undefined ? (
        <Notice kind="info">
          {room.refusal} <PlanLink />
        </Notice>
      ) : (
        room.room !== undefined && <p className="small muted">{room.room}</p>
      )}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Name">
          <TextInput value={name} onChange={setName} placeholder="agent" required autoFocus />
        </Field>
        <Field label="Description" hint="For people.">
          <TextInput value={description} onChange={setDescription} />
        </Field>
        <Field
          label="Image"
          hint="A tag is pinned to its digest when the workload is added. The registry must be one this install pulls from."
        >
          <TextInput value={image} onChange={setImage} placeholder="ghcr.io/acme/agent:1.0" mono required />
        </Field>
        <div className="two">
          <Field label="Virtual CPUs">
            <TextInput value={cpus} onChange={setCpus} type="number" />
          </Field>
          <Field label="Memory (MiB)">
            <TextInput value={memory} onChange={setMemory} type="number" />
          </Field>
        </div>
        <Field label="Copies" hint={COPIES_HINT}>
          <TextInput value={count} onChange={setCount} type="number" />
        </Field>
        <Field
          label="Answers as"
          hint="The model or toolkit names it is found by, separated by commas. Each is this workload's alone in the workspace."
        >
          <TextInput value={names} onChange={setNames} placeholder="fast, gpt-5" mono />
        </Field>
        <div className="field">
          <span className="label">Configuration</span>
          <PairsEditor pairs={pairs} onChange={setPairs} />
          <span className="hint">
            Plain values it starts with, as environment variables. A secret goes in Secrets under the
            workload&apos;s own path, never here.
          </span>
        </div>
        <div className="field">
          <span className="label">May reach</span>
          <RulesEditor rules={rules} onChange={setRules} />
          <span className="hint">
            Nothing outside Impire unless listed here, each rule applied as written.
          </span>
        </div>
      </form>
    </FormPage>
  );
}
